import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { MapPin, Phone, ArrowRight, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ServiceAreaPage = () => {
  const [serviceData, setServiceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        const response = await axios.get(`${API}/service-areas`);
        setServiceData(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchServiceAreas();
  }, []);

  const allStates = [
    { name: "Central Orlando", cities: ["Downtown Orlando", "College Park", "Thornton Park", "Milk District", "SODO"], primary: true },
    { name: "Winter Park", cities: ["Winter Park Village", "Baldwin Park", "Maitland", "Eatonville"], primary: true },
    { name: "East Orlando", cities: ["Waterford Lakes", "Avalon Park", "Lake Nona", "Stoneybrook"], primary: true },
    { name: "West Orlando", cities: ["Dr. Phillips", "Windermere", "Winter Garden", "Ocoee", "MetroWest"], primary: true },
    { name: "South Orlando", cities: ["Kissimmee", "Celebration", "Hunter's Creek", "Meadow Woods"], primary: false },
    { name: "North Orlando", cities: ["Altamonte Springs", "Longwood", "Casselberry", "Sanford", "Oviedo"], primary: false },
    { name: "Osceola County", cities: ["St. Cloud", "Poinciana"], primary: false },
    { name: "Seminole County", cities: ["Lake Mary", "Heathrow", "Winter Springs"], primary: false },
  ];

  return (
    <main className="pt-20">
      {/* Hero Banner */}
      <div className="bg-brand-navy py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="inline-block text-sm font-medium uppercase tracking-widest text-brand-orange mb-2">
            Coverage Area
          </span>
          <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight uppercase text-white">
            Service Areas
          </h1>
          <p className="font-body text-lg text-white/70 mt-4 max-w-2xl">
            Proudly serving communities across the United States with quality construction services
          </p>
        </div>
      </div>

      {/* Map Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Interactive Map */}
            <div className="relative">
              <div className="bg-brand-ice rounded-sm p-8 border border-border">
                {/* US Map SVG - Enhanced */}
                <svg
                  viewBox="0 0 960 600"
                  className="w-full h-auto"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Simplified US Map outline */}
                  <path
                    d="M158,295 L183,282 L233,295 L258,267 L258,242 L295,217 L308,179 L358,154 L408,154 L445,129 L483,129 L533,104 L570,117 L620,92 L645,117 L695,104 L720,129 L758,129 L795,154 L820,167 L845,154 L870,179 L858,217 L870,255 L858,295 L845,330 L820,355 L795,367 L758,355 L720,367 L683,380 L645,392 L608,380 L570,392 L533,405 L495,392 L458,405 L420,417 L383,405 L345,417 L308,430 L270,417 L233,430 L195,442 L158,430 L133,405 L108,367 L95,330 L108,295 Z"
                    fill="#f8fafc"
                    stroke="#334155"
                    strokeWidth="2"
                  />

                  {/* Primary Service Region Highlights */}
                  <ellipse cx="280" cy="380" rx="100" ry="60" fill="#F59E0B" fillOpacity="0.3" />
                  <ellipse cx="380" cy="280" rx="70" ry="80" fill="#F59E0B" fillOpacity="0.3" />
                  <ellipse cx="200" cy="340" rx="50" ry="60" fill="#F59E0B" fillOpacity="0.3" />
                  <ellipse cx="150" cy="280" rx="50" ry="90" fill="#F59E0B" fillOpacity="0.3" />

                  {/* State markers with labels */}
                  {[
                    { x: 280, y: 400, label: "TX", primary: true },
                    { x: 360, y: 260, label: "CO", primary: true },
                    { x: 220, y: 350, label: "AZ", primary: true },
                    { x: 130, y: 280, label: "CA", primary: true },
                    { x: 200, y: 280, label: "NV", primary: false },
                    { x: 270, y: 340, label: "NM", primary: false },
                    { x: 320, y: 250, label: "UT", primary: false },
                    { x: 140, y: 180, label: "WA", primary: false },
                    { x: 130, y: 220, label: "OR", primary: false },
                  ].map((state) => (
                    <g key={state.label} transform={`translate(${state.x}, ${state.y})`}>
                      <circle cx="0" cy="0" r={state.primary ? 12 : 8} fill={state.primary ? "#000000" : "#64748b"} />
                      <circle cx="0" cy="0" r={state.primary ? 6 : 4} fill="#F59E0B" />
                      <text
                        x="18"
                        y="5"
                        fill="#334155"
                        style={{ fontSize: "14px", fontWeight: "bold" }}
                      >
                        {state.label}
                      </text>
                    </g>
                  ))}

                  {/* Headquarters marker */}
                  <g transform="translate(280, 400)">
                    <circle cx="0" cy="0" r="20" fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="4 2" />
                  </g>
                </svg>

                {/* Legend */}
                <div className="flex flex-wrap items-center justify-center gap-6 mt-8 pt-6 border-t border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-brand-navy" />
                    <span className="text-sm text-brand-slate">Primary Coverage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-brand-slate" />
                    <span className="text-sm text-brand-slate">Extended Coverage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-dashed border-brand-orange" />
                    <span className="text-sm text-brand-slate">Headquarters</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Coverage Details */}
            <div className="space-y-8">
              {/* Headquarters Card */}
              <div className="bg-brand-navy rounded-sm p-8 text-white">
                <h3 className="font-heading text-2xl font-bold uppercase mb-6">
                  Service Center
                </h3>
                <div className="flex items-start gap-4 mb-6">
                  <MapPin className="w-6 h-6 text-brand-orange flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-lg">Orlando, Florida</p>
                    <p className="text-white/70">Serving Central FL</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-6 border-t border-white/20">
                  <Phone className="w-5 h-5 text-brand-orange" />
                  <a href="tel:+14079494656" className="hover:text-brand-orange transition-colors text-lg">
                    (407) 949-4656
                  </a>
                </div>
              </div>

              {/* Not in our area? */}
              <div className="bg-brand-ice rounded-sm p-6 border border-border">
                <h4 className="font-heading font-bold text-brand-navy uppercase mb-2">
                  Not in our service area?
                </h4>
                <p className="text-sm text-brand-slate mb-4">
                  Contact us anyway! We may be able to accommodate projects outside our standard coverage area.
                </p>
                <Link to="/contact">
                  <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white">
                    Contact Us
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* States & Cities List */}
      <section className="py-24 bg-brand-ice">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-brand-navy">
              Cities We Serve
            </h2>
            <p className="font-body text-lg text-brand-slate mt-4">
              Comprehensive coverage across Greater Orlando metro area
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allStates.map((state) => (
              <div
                key={state.name}
                className={`bg-white rounded-sm p-6 border ${
                  state.primary ? 'border-brand-orange/30' : 'border-border'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="font-heading text-xl font-bold uppercase text-brand-navy">
                    {state.name}
                  </h3>
                  {state.primary && (
                    <Badge className="bg-brand-orange text-brand-navy text-xs">Primary</Badge>
                  )}
                </div>
                <ul className="space-y-2">
                  {state.cities.map((city) => (
                    <li key={city} className="flex items-center gap-2 text-brand-slate">
                      <CheckCircle className="w-4 h-4 text-brand-orange flex-shrink-0" />
                      {city}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-orange">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-brand-navy mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-lg text-brand-navy/80 mb-8 max-w-2xl mx-auto">
            Get a free estimate for your painting project today.
          </p>
          <Link to="/quote">
            <Button
              size="lg"
              className="bg-brand-navy text-white font-heading font-bold uppercase tracking-wide hover:bg-brand-navy/90 h-14 px-10"
            >
              Get Free Estimate
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};
